import React, { useState, useEffect } from 'react';
import './TestSuiteManagement.css';
import { ShimmerCard, ShimmerListItem } from '../Shimmer';
import ConfirmModal from '../ConfirmModal';
import Pagination from '../Pagination';
import usePagination from '../../hooks/usePagination';
import { API_BASE_URL } from '../../utils/api';
import { normalizeDate } from '../../utils/dates';

function TestSuiteManagement({ onSelectTest, selectedApplication }) {
  const [testSuites, setTestSuites] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddTestModal, setShowAddTestModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newSuiteName, setNewSuiteName] = useState('');
  const [newSuiteDescription, setNewSuiteDescription] = useState('');
  const [availableTestCases, setAvailableTestCases] = useState([]);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [draggedTestCase, setDraggedTestCase] = useState(null);
  const [draggedSuite, setDraggedSuite] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSuiteName, setEditSuiteName] = useState('');
  const [editSuiteDescription, setEditSuiteDescription] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'cards'
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null, type: 'warning' });
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Pagination for test suites
  const suitePagination = usePagination(testSuites, 10);
  
  // Pagination for test cases in selected suite
  const testCasePagination = usePagination(selectedSuite?.testCases || [], 10);

  // Refresh component and show success message
  const refreshComponentWithMessage = (message) => {
    setSuccessMessage(message);
    setRefreshTrigger(prev => prev + 1);
    
    // Auto-hide success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };

  // Get suite icon based on suite name
  const getSuiteIcon = (suiteName) => {
    const name = suiteName.toLowerCase();
    if (name.includes('auth') || name.includes('login')) {
      return 'lock';
    } else if (name.includes('checkout') || name.includes('cart')) {
      return 'cart';
    } else if (name.includes('profile') || name.includes('user')) {
      return 'user';
    } else if (name.includes('payment') || name.includes('gateway')) {
      return 'card';
    } else if (name.includes('search') || name.includes('filter')) {
      return 'search';
    }
    return 'default';
  };

  // Fetch all test suites
  const fetchTestSuites = async () => {
    if (!selectedApplication) {
      setTestSuites([]);
      setLoading(false);
      return;
    }
    
    const startTime = Date.now();
    const minLoadingTime = 2000; // 2 seconds minimum
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-suites?application_id=${selectedApplication.id}`);
      const data = await response.json();
      if (data.success) {
        setTestSuites(data.testSuites);
      } else {
        setError(data.error || 'Failed to fetch test suites');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      // Ensure minimum 2 seconds loading time
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minLoadingTime - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  };

  // Fetch all test cases
  const fetchTestCases = async () => {
    try {
      if (!selectedApplication) {
        setTestCases([]);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/test-cases?limit=1000&application_id=${selectedApplication.id}`);
      const data = await response.json();
      if (data.success) {
        setTestCases(data.testCases);
      }
    } catch (err) {
      console.error('Error fetching test cases:', err);
    }
  };

  // Fetch test suite details
  const fetchSuiteDetails = async (suiteId) => {
    if (!suiteId) return;
    
    if (!selectedApplication) {
      console.warn('Cannot fetch suite details: no application selected');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-suites/${suiteId}?application_id=${selectedApplication.id}`);
      const data = await response.json();
      if (data.success) {
        setSelectedSuite(data.testSuite);
        setError(null); // Clear any previous errors
        console.log('Suite details fetched:', data.testSuite.name, 'with', data.testSuite.testCases?.length || 0, 'test cases');
      } else {
        console.error('Failed to fetch suite details:', data.error);
        setError(data.error || 'Failed to fetch suite details');
        setSelectedSuite(null); // Clear selected suite if fetch failed
      }
    } catch (err) {
      console.error('Error fetching suite details:', err);
      setError(err.message);
      setSelectedSuite(null); // Clear selected suite on error
    }
  };

  useEffect(() => {
    if (selectedApplication) {
      fetchTestSuites();
      fetchTestCases();
      // Clear selected suite when application changes
      setSelectedSuite(null);
      setError(null);
      setSuccessMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApplication, refreshTrigger]);

  // Reset pagination when data changes
  useEffect(() => {
    suitePagination.resetPage();
    // Deliberate: keyed on the count only. The pagination object is rebuilt
    // on every render, so listing it would reset the page constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testSuites.length]);

  useEffect(() => {
    testCasePagination.resetPage();
    // Deliberate: keyed on the count only. The pagination object is rebuilt
    // on every render, so listing it would reset the page constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSuite?.testCases?.length]);

  const handleCreateSuite = async () => {
    if (!newSuiteName.trim()) {
      setError('Suite name is required');
      return;
    }

    if (!selectedApplication) {
      setError('Please select an application first');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/test-suites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newSuiteName.trim(),
          description: newSuiteDescription.trim() || null,
          application_id: selectedApplication.id,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setTestSuites([...testSuites, data.testSuite]);
        setShowCreateModal(false);
        setNewSuiteName('');
        setNewSuiteDescription('');
        setError(null);
        
        // Show success message and refresh component
        refreshComponentWithMessage(`Successfully created test suite "${data.testSuite.name}"`);
      } else {
        setError(data.error || 'Failed to create test suite');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteSuite = async (suiteId) => {
    const suite = testSuites.find(s => s.id === suiteId);
    const suiteName = suite?.name || 'this test suite';
    
    setConfirmModal({
      isOpen: true,
      message: `Are you sure you want to delete "${suiteName}"? This action cannot be undone and will remove all test cases from this suite.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'danger' });
        await performDeleteSuite(suiteId);
      }
    });
  };

  const performDeleteSuite = async (suiteId) => {

    try {
      // Get suite name before deleting for success message
      const suiteToDelete = testSuites.find(s => s.id === suiteId);
      const suiteName = suiteToDelete ? suiteToDelete.name : 'test suite';
      
      const response = await fetch(`${API_BASE_URL}/api/test-suites/${suiteId}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      if (data.success) {
        setTestSuites(testSuites.filter(s => s.id !== suiteId));
        if (selectedSuite && selectedSuite.id === suiteId) {
          setSelectedSuite(null);
        }
        
        // Show success message and refresh component
        refreshComponentWithMessage(`Successfully deleted test suite "${suiteName}"`);
      } else {
        setError(data.error || 'Failed to delete test suite');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSelectSuite = (suite) => {
    console.log('[handleSelectSuite] Selecting suite:', suite);
    
    // Ensure suite has required properties
    if (!suite || !suite.id) {
      console.error('[handleSelectSuite] Invalid suite object:', suite);
      setError('Invalid test suite. Please try again.');
      return;
    }
    
    // Set the suite immediately for UI responsiveness
    // Ensure suite has all basic properties
    const suiteWithProps = {
      id: suite.id,
      name: suite.name || 'Unnamed Suite',
      description: suite.description || null,
      application_id: suite.application_id || selectedApplication?.id,
      testCases: suite.testCases || [],
      test_case_count: suite.test_case_count || 0,
      created_at: suite.created_at,
      updated_at: suite.updated_at
    };
    
    console.log('[handleSelectSuite] Setting selectedSuite:', suiteWithProps);
    setSelectedSuite(suiteWithProps);
    setError(null); // Clear any previous errors
    // Then fetch full details with test cases
    fetchSuiteDetails(suite.id);
  };

  const handleEditSuite = (suite = null) => {
    const suiteToEdit = suite || selectedSuite;
    if (!suiteToEdit) return;
    setEditSuiteName(suiteToEdit.name || '');
    setEditSuiteDescription(suiteToEdit.description || '');
    setShowEditModal(true);
    setError(null);
    // If editing from list, select the suite first
    if (suite && suite.id !== selectedSuite?.id) {
      setSelectedSuite(suite);
    }
  };

  const handleUpdateSuite = async () => {
    if (!selectedSuite) return;
    
    if (!editSuiteName.trim()) {
      setError('Suite name is required');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/test-suites/${selectedSuite.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editSuiteName.trim(),
          description: editSuiteDescription.trim() || null,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setTestSuites(testSuites.map(s => s.id === selectedSuite.id ? data.testSuite : s));
        setSelectedSuite(data.testSuite);
        setShowEditModal(false);
        setEditSuiteName('');
        setEditSuiteDescription('');
        setError(null);
        // Refresh suite details to get updated test cases count
        fetchSuiteDetails(data.testSuite.id);
        
        // Show success message and refresh component
        refreshComponentWithMessage(`Successfully updated test suite "${data.testSuite.name}"`);
      } else {
        setError(data.error || 'Failed to update test suite');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddTestCases = async (suite = null) => {
    console.log('[handleAddTestCases] called', { suite, selectedSuite, selectedApplication, testSuites: testSuites.length });
    
    if (!selectedApplication) {
      setError('Please select an application first');
      return;
    }
    
    // Determine which suite to use - try multiple sources
    let suiteToUse = suite || selectedSuite;
    
    // If no suite provided and selectedSuite is null/invalid, try to find it from the list
    if (!suiteToUse || !suiteToUse.id) {
      // If we have test suites in the list and only one, use it
      if (testSuites.length === 1) {
        suiteToUse = testSuites[0];
        console.log('[handleAddTestCases] Using only available suite from list:', suiteToUse);
      } else if (testSuites.length > 0 && selectedSuite) {
        // Try to find the suite by ID in the list
        const suiteFromList = testSuites.find(s => s.id === selectedSuite.id);
        if (suiteFromList) {
          suiteToUse = suiteFromList;
          console.log('[handleAddTestCases] Found suite in list:', suiteToUse);
        }
      }
    }
    
    // Final check - if still no valid suite
    if (!suiteToUse) {
      console.error('[handleAddTestCases] No suite selected or available');
      setError('Please select a test suite first by clicking on it in the list');
      return;
    }
    
    // Ensure suite has an ID - if not, try to get it from the list
    if (!suiteToUse.id) {
      // Try to find by name if we have a name
      if (suiteToUse.name) {
        const suiteByName = testSuites.find(s => s.name === suiteToUse.name);
        if (suiteByName) {
          suiteToUse = suiteByName;
          console.log('[handleAddTestCases] Found suite by name:', suiteToUse);
        }
      }
      
      // If still no ID, it's invalid
      if (!suiteToUse.id) {
        console.error('[handleAddTestCases] Suite missing ID after all attempts', suiteToUse);
        setError('Invalid test suite. Please click on a test suite in the list to select it first.');
        return;
      }
    }
    
    // Ensure suite has a name - get it from the list if not present
    let suiteName = suiteToUse.name;
    if (!suiteName) {
      // Try to find the suite in the testSuites list
      const suiteFromList = testSuites.find(s => s.id === suiteToUse.id);
      if (suiteFromList && suiteFromList.name) {
        suiteName = suiteFromList.name;
        suiteToUse = { ...suiteToUse, name: suiteName };
        console.log('[handleAddTestCases] Found suite name from list:', suiteName);
      }
    }
    
    if (!suiteName) {
      console.error('[handleAddTestCases] Suite missing name', { suiteToUse, testSuites });
      setError('Cannot add test cases: test suite name is missing. Please try selecting the suite again.');
      return;
    }
    
    // Ensure we have the latest suite details with test cases
    let suiteWithDetails = { ...suiteToUse, name: suiteName };
    let suiteTestCaseIds = [];
    
    // Try to fetch suite details to get the latest test cases list
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-suites/${suiteToUse.id}?application_id=${selectedApplication.id}`);
      const data = await response.json();
      if (data.success && data.testSuite) {
        suiteWithDetails = data.testSuite;
        suiteTestCaseIds = suiteWithDetails.testCases ? suiteWithDetails.testCases.map(tc => tc.id) : [];
        if (!suite || suite.id === selectedSuite?.id) {
          setSelectedSuite(suiteWithDetails);
        }
        setError(null); // Clear any previous errors
        console.log('[handleAddTestCases] Fetched suite details:', suiteWithDetails.name, 'with', suiteTestCaseIds.length, 'test cases');
      } else {
        console.warn('[handleAddTestCases] Failed to fetch suite details, using suite from list:', data.error);
        // If fetch fails, use the suite from the list and assume no test cases are in it yet
        suiteTestCaseIds = [];
        // Ensure suite has name even if fetch failed
        suiteWithDetails = { ...suiteToUse, name: suiteName, testCases: [] };
        if (!selectedSuite || selectedSuite.id !== suiteToUse.id) {
          setSelectedSuite(suiteWithDetails);
        }
        // Don't return - continue to open the modal
      }
    } catch (err) {
      console.warn('[handleAddTestCases] Error fetching suite details, using suite from list:', err);
      // If fetch fails, use the suite from the list and assume no test cases are in it yet
      suiteTestCaseIds = [];
      // Ensure suite has name even if fetch failed
      suiteWithDetails = { ...suiteToUse, name: suiteName, testCases: [] };
      if (!selectedSuite || selectedSuite.id !== suiteToUse.id) {
        setSelectedSuite(suiteWithDetails);
      }
      // Don't return - continue to open the modal
    }
    
    // Final validation - ensure we have a valid suite with name
    if (!suiteWithDetails || !suiteWithDetails.name) {
      console.error('[handleAddTestCases] Cannot open modal: suite is missing or has no name', {
        suiteWithDetails,
        suiteToUse,
        selectedSuite
      });
      setError('Cannot add test cases: test suite information is incomplete. Please try selecting the suite again.');
      return;
    }
    
    // Set selectedSuite to ensure it's available in the modal
    setSelectedSuite(suiteWithDetails);
    
    // Get test cases not already in the suite
    const available = testCases.filter(tc => !suiteTestCaseIds.includes(tc.id));
    console.log('[handleAddTestCases] Available test cases:', available.length, 'Total test cases:', testCases.length, 'Suite test case IDs:', suiteTestCaseIds);
    setAvailableTestCases(available);
    setSelectedTestCases([]);
    setError(null); // Clear any errors before opening modal
    
    setShowAddTestModal(true);
    console.log('[handleAddTestCases] Modal opened, selectedSuite:', suiteWithDetails.name);
  };

  const handleAddSelectedTestCases = async () => {
    if (!selectedSuite || selectedTestCases.length === 0) return;
    
    if (!selectedApplication) {
      setError('Please select an application first');
      return;
    }

    try {
      const promises = selectedTestCases.map(testCaseId =>
        fetch(`${API_BASE_URL}/api/test-suites/${selectedSuite.id}/test-cases?application_id=${selectedApplication.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ testCaseId }),
        }).then(async (response) => {
          const data = await response.json();
          if (!response.ok || !data.success) {
            throw new Error(data.error || `Failed to add test case ${testCaseId}`);
          }
          return data;
        })
      );

      await Promise.all(promises);
      
      // Get test case names for success message
      const addedTestCaseNames = availableTestCases
        .filter(tc => selectedTestCases.includes(tc.id))
        .map(tc => tc.name);
      
      // Refresh suite details after adding
      await fetchSuiteDetails(selectedSuite.id);
      setShowAddTestModal(false);
      setSelectedTestCases([]);
      setError(null);
      
      // Show success message and refresh component
      const message = selectedTestCases.length === 1 
        ? `Successfully added "${addedTestCaseNames[0]}" to the test suite`
        : `Successfully added ${selectedTestCases.length} test cases to the test suite: ${addedTestCaseNames.join(', ')}`;
      
      refreshComponentWithMessage(message);
      
    } catch (err) {
      console.error('Error adding test cases to suite:', err);
      setError(err.message || 'Failed to add test cases to suite');
    }
  };

  const handleRemoveTestCase = async (testCaseId) => {
    setConfirmModal({
      isOpen: true,
      message: 'Are you sure you want to remove this test case from the suite?',
      type: 'warning',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'warning' });
        await performRemoveTestCase(testCaseId);
      }
    });
  };

  const performRemoveTestCase = async (testCaseId) => {

    try {
      // Get test case name before removing for success message
      const testCaseToRemove = selectedSuite.testCases.find(tc => tc.id === testCaseId);
      const testCaseName = testCaseToRemove ? testCaseToRemove.name : 'test case';
      
      const response = await fetch(
        `${API_BASE_URL}/api/test-suites/${selectedSuite.id}/test-cases/${testCaseId}`,
        { method: 'DELETE' }
      );

      const data = await response.json();
      if (data.success) {
        fetchSuiteDetails(selectedSuite.id);
        
        // Show success message and refresh component
        refreshComponentWithMessage(`Successfully removed "${testCaseName}" from the test suite`);
      } else {
        setError(data.error || 'Failed to remove test case');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReorderTestCases = async (testCaseIds) => {
    if (!selectedSuite) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/test-suites/${selectedSuite.id}/reorder`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ testCaseIds }),
        }
      );

      const data = await response.json();
      if (data.success) {
        fetchSuiteDetails(selectedSuite.id);
        
        // Show success message for reordering
        refreshComponentWithMessage('Test cases reordered successfully');
      } else {
        setError(data.error || 'Failed to reorder test cases');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReorderSuites = async (suiteIds) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-suites/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ suiteIds }),
      });

      const data = await response.json();
      if (data.success) {
        fetchTestSuites();
        
        // Show success message for reordering
        refreshComponentWithMessage('Test suites reordered successfully');
      } else {
        setError(data.error || 'Failed to reorder test suites');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Drag and drop handlers for test cases
  const handleTestCaseDragStart = (e, index) => {
    setDraggedTestCase(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleTestCaseDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleTestCaseDragEnd = () => {
    setDraggedTestCase(null);
    setDragOverIndex(null);
  };

  const handleTestCaseDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedTestCase === null || draggedTestCase === dropIndex) {
      setDraggedTestCase(null);
      setDragOverIndex(null);
      return;
    }

    const testCases = [...selectedSuite.testCases];
    const draggedItem = testCases[draggedTestCase];
    testCases.splice(draggedTestCase, 1);
    testCases.splice(dropIndex, 0, draggedItem);

    const testCaseIds = testCases.map(tc => tc.id);
    handleReorderTestCases(testCaseIds);
    setDraggedTestCase(null);
    setDragOverIndex(null);
  };

  // Drag and drop handlers for test suites
  const handleSuiteDragStart = (e, index) => {
    setIsDragging(true);
    setDraggedSuite(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleSuiteDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleSuiteDragEnd = () => {
    // Use setTimeout to prevent click event from firing after drag
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
    setDraggedSuite(null);
    setDragOverIndex(null);
  };

  const handleSuiteDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedSuite === null || draggedSuite === dropIndex) {
      setDraggedSuite(null);
      setDragOverIndex(null);
      setIsDragging(false);
      return;
    }

    const suites = [...testSuites];
    const draggedItem = suites[draggedSuite];
    suites.splice(draggedSuite, 1);
    suites.splice(dropIndex, 0, draggedItem);

    const suiteIds = suites.map(s => s.id);
    handleReorderSuites(suiteIds);
    setDraggedSuite(null);
    setDragOverIndex(null);
    setIsDragging(false);
  };

  if (loading) {
    return (
      <div className="test-suite-management">
        <div className="test-suite-header">
          <div className="shimmer-line shimmer-title"></div>
          <div className="shimmer-button shimmer-create-button"></div>
        </div>
        <div className="test-suite-layout">
          <div className="suite-list-container">
            <div className="suite-list">
              {Array.from({ length: 5 }).map((_, index) => (
                <ShimmerCard key={`shimmer-${index}`} height="120px" />
              ))}
            </div>
          </div>
          <div className="test-suite-details">
            <div className="shimmer-overview-section">
              <div className="shimmer-overview-section-header">
                <div className="shimmer-line shimmer-section-title"></div>
                <div className="shimmer-button shimmer-add-button"></div>
              </div>
              <div className="section-content">
                {Array.from({ length: 3 }).map((_, index) => (
                  <ShimmerListItem key={`shimmer-${index}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="test-suite-management">
      <div className="test-suite-header">
        <h1>Test Suite Management</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 5H16M4 10H16M4 15H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card View"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="3" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="11" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="3" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="11" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </button>
          </div>
          <button className="btn-create-suite" onClick={() => setShowCreateModal(true)}>
            + Create Test Suite
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4"/>
            <circle cx="12" cy="12" r="10"/>
          </svg>
          {successMessage}
          <button onClick={() => setSuccessMessage('')}>×</button>
        </div>
      )}

      <div className="test-suite-content">
        {viewMode === 'list' ? (
          <div className="test-suite-layout">
            <div className="suite-list-container">
              <div className="suite-list">
                <h2>Test Suites ({testSuites.length})</h2>
                {testSuites.length === 0 ? (
                  <div className="empty-state">
                    <p>No test suites created yet.</p>
                    <button className="btn-create-suite" onClick={() => setShowCreateModal(true)}>
                      Create Your First Suite
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="suite-list">
                      {suitePagination.currentData.map((suite, index) => (
                <div
                  key={suite.id}
                  className={`suite-item ${selectedSuite?.id === suite.id ? 'active' : ''} ${draggedSuite === index ? 'dragging' : ''} ${dragOverIndex === index && draggedSuite !== index ? 'drag-over' : ''}`}
                  draggable
                  onDragStart={(e) => handleSuiteDragStart(e, index)}
                  onDragOver={(e) => handleSuiteDragOver(e, index)}
                  onDragEnd={handleSuiteDragEnd}
                  onDrop={(e) => handleSuiteDrop(e, index)}
                  onClick={(e) => {
                    // Prevent click if we just finished dragging
                    if (isDragging) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    handleSelectSuite(suite);
                  }}
                >
                  <div 
                    className="drag-handle" 
                    title="Drag to reorder"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="9" cy="12" r="1"></circle>
                      <circle cx="9" cy="5" r="1"></circle>
                      <circle cx="9" cy="19" r="1"></circle>
                      <circle cx="15" cy="12" r="1"></circle>
                      <circle cx="15" cy="5" r="1"></circle>
                      <circle cx="15" cy="19" r="1"></circle>
                    </svg>
                  </div>
                  <div className="suite-info">
                    <h3>{suite.name}</h3>
                    {suite.description && <p className="suite-description">{suite.description}</p>}
                    <div className="suite-meta">
                      <span>{suite.test_case_count || 0} test cases</span>
                      <span>•</span>
                      <span>{(normalizeDate(suite.created_at) || new Date()).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      className="btn-edit-suite"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditSuite(suite);
                      }}
                      title="Edit suite"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      className="btn-delete-suite"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSuite(suite.id);
                      }}
                      title="Delete suite"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                      ))}
                    </div>
                    
                    {/* Pagination for test suites */}
                    {suitePagination.shouldShowPagination && (
                      <Pagination
                        currentPage={suitePagination.currentPage}
                        totalPages={suitePagination.totalPages}
                        onPageChange={suitePagination.handlePageChange}
                        totalItems={suitePagination.totalItems}
                        itemsPerPage={suitePagination.itemsPerPage}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="test-suite-details">
          {selectedSuite ? (
            <>
              <div className="suite-details-header">
                <div>
                  <h2>{selectedSuite.name}</h2>
                  {selectedSuite.description && (
                    <p className="suite-description">{selectedSuite.description}</p>
                  )}
                </div>
                <button className="btn-add-test" onClick={handleAddTestCases}>
                  + Add Test Cases
                </button>
              </div>

              <div className="suite-test-cases">
                <h3>Test Cases ({selectedSuite.testCases?.length || 0})</h3>
                {!selectedSuite.testCases || selectedSuite.testCases.length === 0 ? (
                  <div className="empty-state">
                    <p>No test cases in this suite.</p>
                  </div>
                ) : (
                  <>
                    <div className="test-case-list">
                      <div className="execution-flow-header">
                        <span className="flow-label">Execution Flow:</span>
                        <span className="flow-description">These test cases will run sequentially in one browser session</span>
                      </div>
                      {testCasePagination.currentData.map((testCase, index) => (
                      <div
                        key={testCase.id}
                        className={`test-case-item ${draggedTestCase === index ? 'dragging' : ''} ${dragOverIndex === index && draggedTestCase !== index ? 'drag-over' : ''}`}
                        draggable
                        onDragStart={(e) => handleTestCaseDragStart(e, index)}
                        onDragOver={(e) => handleTestCaseDragOver(e, index)}
                        onDragEnd={handleTestCaseDragEnd}
                        onDrop={(e) => handleTestCaseDrop(e, index)}
                      >
                        <div className="drag-handle" title="Drag to reorder">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="9" cy="12" r="1"></circle>
                            <circle cx="9" cy="5" r="1"></circle>
                            <circle cx="9" cy="19" r="1"></circle>
                            <circle cx="15" cy="12" r="1"></circle>
                            <circle cx="15" cy="5" r="1"></circle>
                            <circle cx="15" cy="19" r="1"></circle>
                          </svg>
                        </div>
                        <div className="test-case-info">
                          <span className="test-case-number">{index + 1}</span>
                          <div className="test-case-details">
                            <h4>{testCase.name}</h4>
                            <p className="test-case-meta">
                              {testCase.actual_step_count || 0} steps
                              {testCase.url && ` • ${testCase.url}`}
                            </p>
                          </div>
                        </div>
                        <div className="test-case-actions">
                          <button
                            className="btn-view-test"
                            onClick={() => onSelectTest && onSelectTest(testCase)}
                            title="View test"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                          <button
                            className="btn-remove-test"
                            onClick={() => handleRemoveTestCase(testCase.id)}
                            title="Remove from suite"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                    </div>
                    
                    {/* Pagination for test cases */}
                    {testCasePagination.shouldShowPagination && (
                      <Pagination
                        currentPage={testCasePagination.currentPage}
                        totalPages={testCasePagination.totalPages}
                        onPageChange={testCasePagination.handlePageChange}
                        totalItems={testCasePagination.totalItems}
                        itemsPerPage={testCasePagination.itemsPerPage}
                      />
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="empty-selection">
              <p>Select a test suite to view its details</p>
            </div>
          )}
            </div>
          </div>
        ) : (
          <div className="test-suites-grid-view">
            <h2 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: 600, color: '#1F2937' }}>
              Test Suites ({testSuites.length})
            </h2>
            {testSuites.length === 0 ? (
              <div className="empty-state">
                <p>No test suites created yet.</p>
              </div>
            ) : (
              <>
                <div className="test-suites-grid">
                  {suitePagination.currentData.map((suite) => {
                    const iconType = getSuiteIcon(suite.name);
                    
                    return (
                      <div key={suite.id} className="suite-card">
                        <div className="suite-icon">
                          {iconType === 'lock' && (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M9 11V7C9 5.34315 10.3431 4 12 4C13.6569 4 15 5.34315 15 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                          {iconType === 'cart' && (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M3 3H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="9" cy="20" r="1" fill="currentColor"/>
                              <circle cx="20" cy="20" r="1" fill="currentColor"/>
                            </svg>
                          )}
                          {iconType === 'user' && (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                          {iconType === 'card' && (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M2 10H22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                          {iconType === 'search' && (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                          {iconType === 'default' && (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <h3 className="suite-title">{suite.name}</h3>
                        <p className="suite-description">
                          {suite.description || 'No description provided'}
                        </p>
                        <div className="suite-meta">
                          <span className="suite-count">{suite.test_case_count || 0} test cases</span>
                        </div>
                        <button 
                          className="btn-add-test-case-card" 
                          onClick={() => handleAddTestCases(suite)}
                        >
                          + Add Test Case
                        </button>
                      </div>
                    );
                  })}

                  {/* Create New Suite Card */}
                  <div className="suite-card create-card" onClick={() => setShowCreateModal(true)}>
                    <div className="create-icon">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path d="M16 8V24M8 16H24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="suite-title">Create New Suite</h3>
                    <p className="suite-description">Group related test cases together</p>
                  </div>
                </div>
                
                {/* Pagination for grid view */}
                {suitePagination.shouldShowPagination && (
                  <Pagination
                    currentPage={suitePagination.currentPage}
                    totalPages={suitePagination.totalPages}
                    onPageChange={suitePagination.handlePageChange}
                    totalItems={suitePagination.totalItems}
                    itemsPerPage={suitePagination.itemsPerPage}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Create Suite Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Test Suite</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Suite Name *</label>
                <input
                  type="text"
                  value={newSuiteName}
                  onChange={(e) => setNewSuiteName(e.target.value)}
                  placeholder="Enter suite name"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newSuiteDescription}
                  onChange={(e) => setNewSuiteDescription(e.target.value)}
                  placeholder="Enter suite description (optional)"
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={handleCreateSuite}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Test Cases Modal */}
      {showAddTestModal && (
        <div className="modal-overlay" onClick={() => setShowAddTestModal(false)}>
          <div className="modal-content add-test-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Test Cases to Suite</h2>
              <button className="modal-close" onClick={() => setShowAddTestModal(false)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {!selectedSuite ? (
                <p>No test suite selected. Please select a test suite first.</p>
              ) : !selectedSuite.name ? (
                <p>Test suite information is incomplete. Please try again.</p>
              ) : testCases.length === 0 ? (
                <p>No test cases available for this application. Please create test cases first.</p>
              ) : availableTestCases.length === 0 ? (
                <p>All test cases are already in "{selectedSuite.name}".</p>
              ) : (
                <>
                  <div className="suite-name-display" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f3f4f6', borderRadius: '4px' }}>
                    <strong>Test Suite:</strong> <span style={{ color: '#10B981', fontWeight: 600 }}>{selectedSuite.name}</span>
                  </div>
                  <p style={{ marginBottom: '1rem' }}>Select test cases to add:</p>
                  <div className="test-case-selection">
                    {availableTestCases.map((testCase) => (
                      <label key={testCase.id} className="test-case-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedTestCases.includes(testCase.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTestCases([...selectedTestCases, testCase.id]);
                            } else {
                              setSelectedTestCases(selectedTestCases.filter(id => id !== testCase.id));
                            }
                          }}
                        />
                        <span>{testCase.name}</span>
                        <span className="test-case-meta-small">
                          ({testCase.actual_step_count || 0} steps)
                        </span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowAddTestModal(false)}>
                Cancel
              </button>
              <button
                className="btn-confirm"
                onClick={handleAddSelectedTestCases}
                disabled={selectedTestCases.length === 0}
              >
                Add {selectedTestCases.length > 0 ? `(${selectedTestCases.length})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Suite Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Test Suite</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Suite Name *</label>
                <input
                  type="text"
                  value={editSuiteName}
                  onChange={(e) => setEditSuiteName(e.target.value)}
                  placeholder="Enter suite name"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editSuiteDescription}
                  onChange={(e) => setEditSuiteDescription(e.target.value)}
                  placeholder="Enter suite description (optional)"
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={handleUpdateSuite}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        title="Confirm"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'warning' })}
        confirmText="Confirm"
        cancelText="Cancel"
        type={confirmModal.type}
      />
    </div>
  );
}

export default TestSuiteManagement;
